<!--suppress XmlDuplicatedId -->
<template>
    <div
        v-if="chapter != null && max_page > 0"
        id="reader"
        class="row flex-column g-0"
        :data-renderer="renderingMode"
        :data-display="displayFit"
        :data-direction="direction"
    >
        <div
            id="reader-main"
            class="col row g-0 flex-column flex-nowrap noselect"
        >
            <noscript>
                <div class="alert alert-danger text-center">
                    JavaScript is required for this reader to work.
                </div>
            </noscript>
            <div class="reader-chapter-header">
                <h1 class="reader-chapter-title">
                    {{
                        formatChapterTitle(
                            chapter.title ? chapter.title : chapter.full_title,
                        )
                    }}
                    {{ comic.title }}
                </h1>
                <div class="reader-chapter-meta">
                    <router-link
                        class="reader-chapter-comic"
                        :to="`/${comic.format_slug}/${comic.slug}`"
                    >
                        {{ comic.title }}
                    </router-link>
                    <span class="reader-chapter-divider">•</span>
                    <span class="reader-chapter-page">
                        {{ reader.__("Page") }} {{ page }} / {{ max_page }}
                    </span>
                </div>
            </div>
            <div id="banner_top" class="text-center"></div>
            <div
                @click="clickPage"
                id="reader-images"
                :rendering="renderingMode"
                class="col-auto row g-0 flex-nowrap m-auto text-center cursor-pointer directional"
            >
                <template v-if="renderingMode === 'single-page'">
                    <div
                        :data-page="page"
                        :style="pageWrapperStyle(page)"
                        :rendering="renderingMode"
                        class="reader-image-wrapper col-auto my-auto justify-content-center align-items-center noselect nodrag row g-0"
                    ></div>
                </template>
                <template v-else-if="renderingMode === 'double-page'">
                    <div
                        :data-page="page"
                        :style="pageWrapperStyle(page)"
                        :rendering="renderingMode"
                        class="reader-image-wrapper col-auto my-auto justify-content-center align-items-center noselect nodrag row g-0"
                    ></div>
                    <div
                        v-if="chapter.pages[page] != null"
                        :data-page="page + 1"
                        :style="pageWrapperStyle(page + 1)"
                        :rendering="renderingMode"
                        class="reader-image-wrapper col-auto my-auto justify-content-center align-items-center noselect nodrag row g-0"
                    ></div>
                </template>
                <template v-else>
                    <div
                        v-for="(ch_page, index) in chapter.pages"
                        :data-page="index + 1"
                        :style="pageWrapperStyle(index + 1)"
                        :rendering="renderingMode"
                        class="reader-image-wrapper col-auto my-auto justify-content-center align-items-center noselect nodrag row g-0"
                    ></div>
                </template>
            </div>
            <div id="banner_bottom" class="text-center"></div>
            <div
                id="reader-page-bar"
                class="col-auto d-none d-lg-flex directional"
            >
                <div id="track" class="cursor-pointer row g-0">
                    <div
                        id="trail"
                        class="position-absolute h-100 noevents"
                        :style="
                            'width: ' +
                            (page === max_page
                                ? (100 * page) / max_page
                                : (100 * (page + renderedPages - 1)) /
                                  max_page) +
                            '%;'
                        "
                    >
                        <div
                            id="thumb"
                            class="h-100"
                            :style="
                                'width: ' +
                                (page === max_page
                                    ? 100 / page
                                    : (100 / (page + renderedPages - 1)) *
                                      renderedPages) +
                                '%;'
                            "
                        ></div>
                    </div>
                    <div id="notches" class="row g-0 h-100 w-100 directional">
                        <template v-for="(ch_page, index) in chapter.pages">
                            <router-link
                                :class="
                                    images[index + 1] == null
                                        ? 'notch col'
                                        : 'notch col loaded'
                                "
                                :data-page="index + 1"
                                @mouseover="setNotch(index + 1)"
                                :to="'#' + (index + 1)"
                                :style="'order: ' + (index + 1) + ';'"
                            >
                                <div @click="needToRefresh = true"></div>
                            </router-link>
                        </template>
                    </div>
                    <div
                        id="notch-display"
                        class="col-auto m-auto px-3 py-1 noevents"
                    >
                        {{ hover_page }} / {{ max_page }}
                    </div>
                </div>
            </div>
            <div class="reader-chapter-nav reader-chapter-nav--bottom">
                <router-link
                    v-if="chapter.previous != null"
                    class="btn btn-secondary reader-chapter-nav__prev"
                    :to="chapter.previous.url"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="
                            display: inline-block;
                            vertical-align: middle;
                            margin-right: 4px;
                        "
                        aria-hidden="true"
                    >
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    {{ reader.__("Previous chapter") }}
                </router-link>
                <router-link
                    v-if="chapter.next != null"
                    class="btn btn-secondary reader-chapter-nav__next"
                    :to="chapter.next.url"
                >
                    {{ reader.__("Next chapter") }}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="
                            display: inline-block;
                            vertical-align: middle;
                            margin-left: 4px;
                        "
                        aria-hidden="true"
                    >
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </router-link>
            </div>
            <div class="reader-chapter-rating" v-if="chapter">
                <span class="reader-chapter-rating__label">{{
                    selectedVote > 0
                        ? reader.__("Thank you for rating!")
                        : reader.__("Did you like this chapter?")
                }}</span>
                <div class="ratings">
                    <button
                        v-for="n in 5"
                        :key="n"
                        type="button"
                        class="star"
                        :class="{ selected: selectedVote >= n }"
                        @click="doVote(n)"
                        :aria-label="n + ' ' + reader.__('stars')"
                    ></button>
                </div>
            </div>
        </div>
        <teleport to="#header .reader-header__center">
            <div class="reader-nav-controls" v-if="comic">
                <router-link
                    class="btn btn-sm btn-secondary reader-nav-btn"
                    :to="`/${comic.format_slug}/${comic.slug}`"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="
                            display: inline-block;
                            vertical-align: middle;
                            margin-right: 4px;
                        "
                    >
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    <span class="reader-nav-btn__text">{{
                        reader.__("Back to title")
                    }}</span>
                </router-link>
                <div
                    class="reader-chapter-select"
                    v-if="comic.chapters && comic.chapters.length > 1"
                >
                    <select
                        class="form-select form-select-sm reader-chapter-dropdown"
                        @change="jumpChapter"
                    >
                        <option
                            v-for="ch in comic.chapters"
                            :key="ch.slug"
                            :value="ch.url + '#1'"
                            :selected="chapter && ch.slug === chapter.slug"
                        >
                            {{ formatChapterTitle(ch.full_title) }}
                        </option>
                    </select>
                </div>
            </div>
        </teleport>
        <teleport to="body">
            <div
                v-if="telegramChannel && !tgBannerHidden"
                class="reader-tg-banner"
            >
                <a
                    :href="telegramChannel"
                    target="_blank"
                    rel="noopener"
                    class="reader-tg-link"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                        style="
                            display: inline-block;
                            vertical-align: middle;
                            flex-shrink: 0;
                        "
                    >
                        <path
                            d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"
                        />
                    </svg>
                    <span>{{ telegramButtonText }}</span>
                </a>
                <button
                    type="button"
                    class="reader-tg-close"
                    @click.stop="tgBannerHidden = true"
                    :aria-label="reader.__('Close')"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </teleport>
        <div
            v-if="comic && comic.reader_button_url && !chapterActionHidden"
            class="reader-chapter-action-wrap"
        >
            <a
                class="reader-chapter-action btn btn-primary"
                :href="comic.reader_button_url"
                target="_blank"
                rel="noopener noreferrer"
            >
                {{ comic.reader_button_title || reader.__("Open") }}
            </a>
            <button
                type="button"
                class="reader-chapter-action-close"
                @click.stop="chapterActionHidden = true"
                :aria-label="reader.__('Close')"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>
        <popup :popupData="popupData"></popup>
    </div>
    <div v-else ref="notfound"><popup :popupData="popupData"></popup></div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import NotFound from "./NotFound.vue";
import Popup from "./Popup.vue";

export default {
    name: "Read",
    components: { NotFound, Popup },
    beforeCreate() {
        // Non-reactive: jQuery .html() manages DOM, Vue reactivity causes errors
        this.images = [];
    },
    mounted() {
        this.setupParams(this.$route.params);
        $("body").addClass("body-reader");
        $("#nav-search").hide();
        $("#results-box").hide();
        $("#nav-filter").hide();
        if (this.hide_header) this.hideHeader();
        if (this.hide_sidebar) this.hideSidebar();

        // Scroll-based header hide/show
        this._lastScrollY = window.scrollY;
        this._headerEl = document.getElementById("header");
        this._scrollHandler = () => {
            const currentY = window.scrollY;
            const delta = currentY - this._lastScrollY;
            if (delta > 10 && currentY > 60) {
                // Scrolling down — hide header
                if (this._headerEl)
                    this._headerEl.classList.add("header-hidden");
            } else if (delta < -5) {
                // Scrolling up — show header
                if (this._headerEl)
                    this._headerEl.classList.remove("header-hidden");
            }
            this._lastScrollY = currentY;
        };
        window.addEventListener("scroll", this._scrollHandler, {
            passive: true,
        });

        this.$store.dispatch("fetchChapter", this.$route.path).then(() => {
            if (this.$store.getters.chapter !== null) {
                this.max_page = this.$store.getters.chapter.pages.length;
                this.setPage(this.$route.hash);
                this.updateViewChapter();
            } else {
                const ComponentClass = Vue.extend(NotFound);
                const instance = new ComponentClass({
                    propsData: { reader: this.reader },
                });
                instance.$mount();
                if (typeof this.$refs.notfound !== "undefined")
                    this.$refs.notfound.appendChild(instance.$el);
                const title = "Error 404 - " + this.reader.SITE_NAME;
                $("title").html(title);
                $('meta[property="og:title"]').attr("content", title);
            }
        });
        this.chapter != null && this.updateCustomHTML();
    },
    beforeDestroy() {
        if (this._scrollHandler) {
            window.removeEventListener("scroll", this._scrollHandler);
        }
        if (this._headerEl) {
            this._headerEl.classList.remove("header-hidden");
        }
    },
    beforeRouteUpdate(to, from) {
        if (from.path !== to.path) {
            this.selectedVote = 0;
            this.setupParams(to.params);
            // If you don't set to null the Observer still has reference
            for (let i = 1; i <= this.max_page; i++) {
                this.images[i] = null;
            }
            this.needToRefresh = true;
            this.$store.dispatch("fetchChapter", to.path).then(() => {
                if (this.$store.getters.chapter !== null) {
                    this.max_page = this.$store.getters.chapter.pages.length;
                    this.setPage(to.hash);
                    this.updateViewChapter();
                }
            });
        } else {
            this.animation = true;
            this.setPage(to.hash);
        }
    },
    updated() {
        if (this.needToRefresh) {
            if (this.renderingMode === "long-strip") {
                for (let i = 1; i <= this.max_page; i++) {
                    $('.reader-image-wrapper[data-page="' + i + '"]').html(
                        this.images[i] ? this.images[i] : null,
                    );
                }
            } else {
                $('.reader-image-wrapper[data-page="' + this.page + '"]').html(
                    this.images[this.page],
                );
                if (
                    this.renderingMode === "double-page" &&
                    this.page < this.max_page
                ) {
                    $(
                        '.reader-image-wrapper[data-page="' +
                            (this.page + 1) +
                            '"]',
                    ).html(
                        this.images[this.page + 1]
                            ? this.images[this.page + 1]
                            : null,
                    );
                }
                this.swipeInit();
            }
            this.scrollTopOfPage();
            this.needToRefresh = false;
        } else if (
            this.firstLoad &&
            this.page > 1 &&
            this.renderingMode === "long-strip"
        ) {
            this.animation = true;
            this.scrollTopOfPage();
        }
        if (
            this.firstLoad &&
            this.$store.getters.chapter &&
            this.$store.getters.chapter.licensed
        ) {
            this.showPopup();
        }
        if (this.chapter != null && (this.needToRefresh || this.firstLoad)) {
            this.updateCustomHTML();
        }
        this.firstLoad = false;
    },
    data() {
        return {
            reader: this.$root,
            viewed: this.$root.getSetting("viewed")
                ? JSON.parse("" + this.$root.getSetting("viewed"))
                : {},
            page: 1,
            max_page: 0,
            hover_page: 1,
            hide_header: parseInt(this.$root.getSetting("hide-header") || 0),
            hide_sidebar: parseInt(this.$root.getSetting("hide-sidebar") || 1),
            displayFit: this.$root.getSetting("displayFit") || "fit-width",
            renderingMode:
                this.$root.getSetting("renderingMode") || "long-strip",
            direction: this.$root.getSetting("direction") || "ltr",
            renderedPages:
                (this.$root.getSetting("renderingMode") || "long-strip") ===
                "double-page"
                    ? 2
                    : 1,
            valueLeft: this.$root.getSetting("direction") === "rtl" ? 1 : -1,
            valueRight: this.$root.getSetting("direction") === "rtl" ? -1 : 1,
            animation: false,
            needToRefresh: true,
            firstLoad: false,
            selectedVote: 0,
            tgBannerHidden: false,
            chapterActionHidden: false,
            popupData: {
                header: this.$root.__("Chapter licensed"),
                body: this.$root.__(
                    "This chapter is licensed and you can't read it.",
                ),
                button: "Ok",
            },
        };
    },
    methods: {
        setupParams(params) {
            if (params.pathMatch) {
                ["vol", "ch", "sub"].forEach((element) => {
                    let index = params.pathMatch.indexOf(element);
                    if (index !== -1) {
                        params[element] = params.pathMatch[index + 1];
                    }
                });
            }
        },
        formatChapterTitle(fullTitle) {
            if (!fullTitle) return "";
            return fullTitle.replace(/^Ch\.?\s*/i, "Глава ");
        },
        setPage(hash) {
            if (this.max_page < 1) return;
            if (hash === "#last" || hash === "") this.animation = false;
            let page;
            let requested_page = hash.substring(1);
            if (
                requested_page === "last" ||
                parseInt(requested_page) > this.max_page
            ) {
                page = this.max_page;
            } else {
                page =
                    isNaN(parseInt(requested_page)) ||
                    parseInt(requested_page) < 1
                        ? 1
                        : parseInt(requested_page);
            }
            this.page = page;
            this.preloadImagesFrom(page);
        },
        jumpPage(e) {
            this.needToRefresh = true;
            this.setPage("#" + e.target.value);
        },
        jumpChapter(e) {
            this.needToRefresh = true;
            this.$router.push(e.target.value);
        },
        setNotch(page) {
            this.hover_page = page;
        },
        hideHeader() {
            $("body").toggleClass("hide-header");
        },
        hideSidebar() {
            $("#reader").toggleClass("hide-sidebar");
        },
        toggleSettings() {
            $("#reader-controls-mode").toggleClass("d-none");
            $("#settings-button").toggleClass("active");
        },
        setSettings(e) {
            const setting = $(e.target).data("setting");
            const value = $(e.target).data("value");
            if (setting === "direction") {
                this.direction = value;
                this.valueLeft = this.direction === "ltr" ? -1 : 1;
                this.valueRight = this.direction === "ltr" ? 1 : -1;
            } else if (setting === "hide-header") {
                this.hide_header = value;
                this.hideHeader();
            } else if (setting === "hide-sidebar") {
                this.hide_sidebar = value;
                this.hideSidebar();
            } else if (setting === "displayFit") {
                this.displayFit = value;
            } else if (setting === "renderingMode") {
                this.renderingMode = value;
                this.renderedPages =
                    this.renderingMode === "double-page" ? 2 : 1;
                this.needToRefresh = true;
            } else if (setting === "reset") {
                this.displayFit = "fit-width";
                this.renderingMode = "long-strip";
                this.direction = "ltr";
                this.valueLeft = -1;
                this.valueRight = 1;
                this.needToRefresh = true;
                localStorage.removeItem("displayFit");
                localStorage.removeItem("renderingMode");
                localStorage.removeItem("direction");
                return;
            } else {
                return;
            }
            localStorage.setItem(setting, value);
            //this.reader.setCookie(setting, value, 3650);
        },
        scrollTopOfPage() {
            $(
                'button[data-bs-target="#navbarSupportedContent"][aria-expanded="true"]',
            ).click();
            if (this.max_page < 1 || !this.animation) return;
            let offset = $("body").hasClass("hide-header")
                ? 0
                : Number(
                      getComputedStyle(document.body, "").fontSize.match(
                          /(\d*(\.\d*)?)px/,
                      )[1],
                  ) * 3.5;
            if (this.renderingMode === "long-strip") {
                canChange = false;
                $("html,body").animate(
                    {
                        scrollTop:
                            $(
                                '.reader-image-wrapper[data-page="' +
                                    this.page +
                                    '"]',
                            ).offset().top - offset,
                    },
                    400,
                    function () {
                        canChange = true;
                    },
                );
            } else {
                $("html,body").scrollTop(
                    $(
                        '.reader-image-wrapper[data-page="' + this.page + '"]',
                    ).offset().top - offset,
                );
            }
            this.animation = false;
        },
        preloadImage(url, i) {
            return new Promise((resolve, reject) => {
                this.images[i] = new Image();
                this.images[i].onload = function () {
                    resolve(url);
                };
                this.images[i].onerror = function () {
                    reject(url);
                };

                this.images[i].draggable = "false";
                this.images[i].className = "noselect nodrag cursor-pointer";
                this.images[i].alt = "Page " + i;
                // LCP: fetchpriority=high для первой страницы
                if (i === 1) {
                    this.images[i].fetchPriority = "high";
                }
                this.images[i].src = url;
            });
        },
        recursivePreload(page_number) {
            if (this.images[page_number] == null) {
                $(
                    '.reader-image-wrapper[data-page="' + page_number + '"]',
                ).html("");
                this.preloadImage(this.getPageUrl(page_number), page_number)
                    .then((successUrl) => {
                        $('.notch[data-page="' + page_number + '"]').addClass(
                            "loaded",
                        );
                        $(
                            '.reader-image-wrapper[data-page="' +
                                page_number +
                                '"]',
                        ).html(this.images[page_number]);
                        if (page_number < this.max_page) {
                            this.recursivePreload(
                                page_number + 1,
                                this.max_page,
                            );
                        }
                    })
                    .catch((errorUrl) => {
                        // Nothing
                    });
            }
        },
        async preloadImagesFrom(page_number) {
            if (page_number == null) return;
            this.recursivePreload(page_number);
        },
        clickPage(e) {
            const pW =
                document.body.clientWidth -
                document.body.scrollWidth +
                $(e.target).closest("#reader-images").width();
            e.clientX < pW / 2
                ? $("#turn-left").click()
                : $("#turn-right").click();
        },
        swipeInit() {
            $(document).ready(function () {
                $("#reader-images").swipe({
                    swipeLeft: function () {
                        $("#reader-images:not([rendering=long-strip])")
                            .length &&
                            $("html,body").scrollTop() > 200 &&
                            $("#turn-right").click();
                    },
                    swipeRight: function () {
                        $("#reader-images:not([rendering=long-strip])")
                            .length &&
                            $("html,body").scrollTop() > 200 &&
                            $("#turn-left").click();
                    },
                });
            });
        },
        updateViewChapter() {
            const comic = this.$store.getters.comic;
            const chapter = this.$store.getters.chapter;
            if (this.max_page > 0) {
                if (typeof this.viewed[comic.slug] === "undefined") {
                    this.viewed[comic.slug] = {};
                }
                this.viewed[comic.slug][chapter.slug_lang_vol_ch_sub] = 1;
                localStorage.setItem("viewed", JSON.stringify(this.viewed));
            }

            // SEO: Формат title "Глава 9 - Джинкс - SiteName"
            const chTitle = this.formatChapterTitle(chapter.full_title);
            const sName = this.reader.SITE_NAME || "MangaReader";
            const title = chTitle + " - " + comic.title + " - " + sName;
            const comicDesc = comic.description
                ? " " + comic.description.substring(0, 200)
                : "";
            const desc =
                "Читать " +
                chTitle +
                " " +
                (comic.format_name || "манги").toLowerCase() +
                " " +
                comic.title +
                " онлайн бесплатно на русском языке." +
                comicDesc;
            const url = this.reader.BASE_URL + chapter.url.replace(/^\//, "");

            $("title").html(title);
            $('meta[property="og:title"]').attr("content", title);
            $('meta[name="description"]').attr("content", desc);
            $('meta[property="og:description"]').attr("content", desc);
            $('meta[name="twitter:title"]').attr("content", title);
            $('meta[name="twitter:description"]').attr("content", desc);
            $('link[rel="canonical"]').attr("href", url);
            $('meta[property="og:url"]').attr("content", url);

            this.firstLoad = true;
            if (
                !this.$root.getSetting("renderingMode") &&
                comic.format_slug === "webtoon"
            ) {
                this.renderingMode = "long-strip";
            }
            this.$store.dispatch("fetchVote", chapter.vote_id).then(() => {
                const voteVal = this.$store.getters.vote(chapter.vote_id);
                if (voteVal) this.selectedVote = voteVal;
            });
        },
        sendVote(e) {
            const vote = parseInt($(e.target).data("vote"));
            this.doVote(vote);
        },
        doVote(n) {
            this.selectedVote = n;
            const votePath = this.$route.path.replace("/read/", "/vote/");
            window.axios.post(this.reader.API_BASE_URL + votePath, {
                vote: n,
                vote_token: this.$store.getters.vote_token,
            });
        },
        getPageUrl(pageNum) {
            const p = this.$store.getters.chapter.pages[pageNum - 1];
            return typeof p === "object" && p !== null ? p.url : p;
        },
        getPageDimensions(pageNum) {
            const p = this.$store.getters.chapter.pages[pageNum - 1];
            if (typeof p === "object" && p !== null && p.width && p.height) {
                return { width: p.width, height: p.height };
            }
            return null;
        },
        pageWrapperStyle(pageNum) {
            const dims = this.getPageDimensions(pageNum);
            let style = "order: " + pageNum + ";";
            if (dims) {
                style +=
                    " aspect-ratio: " + dims.width + " / " + dims.height + ";";
                style += " max-width: " + dims.width + "px;";
            }
            return style;
        },
        showPopup() {
            const modal = new bootstrap.Modal(
                document.getElementById("modal-container"),
            );
            modal.show();
        },
        updateCustomHTML() {
            this.reader.updateCustomHTML("banner_top");
            this.reader.updateCustomHTML("banner_bottom");
            this.reader.updateCustomHTML("reader_html");
        },
    },
    computed: {
        ...mapGetters(["comic", "chapter", "vote", "vote_token"]),
        telegramChannel() {
            return typeof SOCIAL_TELEGRAM_CHANNEL !== "undefined" &&
                SOCIAL_TELEGRAM_CHANNEL
                ? SOCIAL_TELEGRAM_CHANNEL
                : null;
        },
        telegramButtonText() {
            const custom =
                typeof SOCIAL_TELEGRAM_CHANNEL_TEXT !== "undefined" &&
                SOCIAL_TELEGRAM_CHANNEL_TEXT
                    ? SOCIAL_TELEGRAM_CHANNEL_TEXT
                    : null;
            return custom || this.reader.__("Subscribe in Telegram");
        },
    },
};
</script>
